import React, { useState, useEffect } from 'react';
import { 
    Paper, Typography, Box, Chip, Grid, Alert, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Card, CardContent,
    CircularProgress
} from '@mui/material';
import { 
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
    LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar,
    AreaChart, Area
} from 'recharts';
import api from "../../../services/api";
import { red, orange, blue, green, purple, pink, amber, teal, grey } from '@mui/material/colors';
import ErrorIcon from '@mui/icons-material/Error';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import WarningIcon from '@mui/icons-material/Warning';

// =============================================================================
// COMPONENTE: Distribuição por Tipo de Queda (Alarmes Huawei)
// =============================================================================
export function DowntimeByCause() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalIncidents, setTotalIncidents] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/downtime_by_cause');
                setData(response.data.distribution || []);
                setTotalIncidents(response.data.total || 0);
            } catch (error) {
                console.error('Erro ao buscar dados por causa:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Cores específicas para alarmes Huawei OLT
    const CAUSE_COLORS = {
        // Alarmes Críticos Huawei
        'LOS': red[700],           // Loss of Signal - Mais crítico
        'LOSi': red[500],          // Loss of Signal upstream
        'LOF': red[600],           // Loss of Frame
        'LOFi': red[400],          // Loss of Frame upstream
        'LOL': orange[700],        // Loss of Lock
        'LOKI': orange[600],       // Loss of Key
        'LOM': orange[500],        // Loss of Multiframe
        
        // Alarmes de Equipamento
        'EQPT': blue[700],         // Equipment Failure
        'SF-EQPT': blue[600],      // Signal Fail Equipment
        'DGI': blue[500],          // Degraded
        'TEMP': amber[700],        // Temperature Alarm
        'VOLT': amber[600],        // Voltage Alarm
        'CURR': amber[500],        // Current Alarm
        
        // Alarmes de Rede/Fibra
        'FBR': red[800],           // Fiber Break
        'OPT': purple[700],        // Optical Power
        'SD': purple[600],         // Signal Degrade
        'SF': purple[500],         // Signal Fail
        'TIM': pink[600],          // Trace Identifier Mismatch
        
        // Alarmes de Sistema
        'PWR': amber[800],         // Power Failure
        'FAN': teal[600],          // Fan Failure
        'SW': blue[400],           // Software Alarm
        'DB': green[700],          // Database Alarm
        'CONFIG': green[600],      // Configuration Alarm
        
        // Compatibilidade com nomes genéricos
        'Power Failure': amber[800],
        'Fiber Break': red[800],
        'Equipment Failure': blue[700],
        'Unknown': grey[500],
        'Other': grey[400]
    };

    const getColor = (cause, index) => {
        return CAUSE_COLORS[cause] || [red, orange, blue, green, purple, pink, amber, teal][index % 8][600];
    };

    if (loading) return <LoadingCard height={400} />;

    const rows = [...data].sort((a, b) => b.count - a.count);
    const max = rows.length ? rows[0].count : 0;

    return (
        <ChartContainer title="Incidentes por Tipo de Alarme (Huawei OLT)">
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                {totalIncidents.toLocaleString('pt-BR')} incidentes no total
            </Typography>
            {rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nenhum incidente no período.
                </Typography>
            )}
            <Box display="flex" flexDirection="column" gap={1.25}>
                {rows.map((row, index) => {
                    const pct = totalIncidents > 0 ? (row.count / totalIncidents * 100) : 0;
                    const barW = max > 0 ? (row.count / max * 100) : 0;
                    const color = getColor(row.cause, index);
                    return (
                        <Box key={index}>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.25}>
                                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                    <Box width={10} height={10} borderRadius="2px" bgcolor={color} flexShrink={0} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {row.cause}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                                    <strong>{row.count.toLocaleString('pt-BR')}</strong> · {pct.toFixed(1)}%
                                </Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.100', overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${barW}%`, bgcolor: color,
                                           borderRadius: 1, transition: 'width .3s' }} />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Timeline de Incidentes por Tipo
// =============================================================================
export function DowntimeTimeline() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('7d');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await api.get(`/provisioning/dashboard/downtime_timeline?period=${period}`);
                setData(response.data || []);
            } catch (error) {
                console.error('Erro ao buscar timeline:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [period]);

    if (loading) return <LoadingCard height={400} />;

    return (
        <ChartContainer title="Timeline de Incidentes por Tipo de Alarme">
            {/* Filtros de Período */}
            <Box display="flex" gap={1} mb={2}>
                {[
                    { value: '7d', label: '7 dias' },
                    { value: '30d', label: '30 dias' },
                    { value: '90d', label: '90 dias' }
                ].map((option) => (
                    <Chip
                        key={option.value}
                        label={option.label}
                        onClick={() => setPeriod(option.value)}
                        color={period === option.value ? 'primary' : 'default'}
                        variant={period === option.value ? 'filled' : 'outlined'}
                        size="small"
                    />
                ))}
            </Box>

            {(() => {
                // Séries dinâmicas: as causas que REALMENTE vêm nos dados
                // (antes eram 8 chaves fixas LOS/LOF/... que não batiam com
                // as causas reais — dying-gasp/LOSi-LOBi — e o gráfico vinha
                // vazio). Barra empilhada lê bem com poucos dias / 1 ponto.
                const palette = [red[600], orange[600], blue[600], green[600],
                                 purple[600], amber[700], teal[600], pink[500], grey[600]];
                const causes = [...new Set(
                    data.flatMap(d => Object.keys(d).filter(k => k !== 'date'))
                )];
                if (!data.length || !causes.length) {
                    return (
                        <Box height={200} display="flex" alignItems="center"
                             justifyContent="center">
                            <Typography variant="body2" color="text.secondary">
                                Sem incidentes no período selecionado.
                            </Typography>
                        </Box>
                    );
                }
                return (
                    <ResponsiveContainer height={340} width="100%">
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            {causes.map((c, i) => (
                                <Bar key={c} dataKey={c} stackId="a"
                                     fill={palette[i % palette.length]} name={c}
                                     maxBarSize={48} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                );
            })()}
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Análise de Incidentes por Horário
// =============================================================================
export function DowntimeByHour() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/downtime_by_hour');
                setData(response.data || []);
            } catch (error) {
                console.error('Erro ao buscar dados por horário:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    return (
        <ChartContainer title="Incidentes por Horário do Dia">
            <ResponsiveContainer height={300} width="100%">
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id="incidentGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={red[600]} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={red[600]} stopOpacity={0.1}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                        dataKey="hour" 
                        tickFormatter={(hour) => `${hour}:00`}
                    />
                    <YAxis />
                    <Tooltip 
                        formatter={(value) => [`${value} incidentes`, 'Total']}
                        labelFormatter={(hour) => `${hour}:00 - ${hour + 1}:00`}
                    />
                    <Area 
                        type="monotone" 
                        dataKey="incidents" 
                        stroke={red[600]} 
                        fill="url(#incidentGradient)"
                        strokeWidth={2}
                    />
                </AreaChart>
            </ResponsiveContainer>
            
            <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Insight:</strong> Identifique os horários de pico de incidentes para otimizar manutenções preventivas
            </Alert>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Top ONUs com Mais Problemas
// =============================================================================
export function TopProblematicONUs() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/top_problematic_onus');
                setData(response.data || []);
            } catch (error) {
                console.error('Erro ao buscar top ONUs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    return (
        <ChartContainer title="Top 10 - ONUs com Mais Incidentes">
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {data.length === 0 ? (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 200
                        }}
                    >
                        🎉 Nenhuma ONU com incidentes recorrentes!
                    </Alert>
                ) : (
                    data.map((onu, index) => (
                        <Box 
                            key={index} 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center"
                            py={1.5}
                            px={1}
                            borderBottom={index < data.length - 1 ? `1px solid ${grey[200]}` : 'none'}
                            sx={{
                                '&:hover': {
                                    backgroundColor: grey[50],
                                    borderRadius: 1
                                }
                            }}
                        >
                            <Box flex={1}>
                                <Typography variant="body2" fontWeight="bold">
                                    #{index + 1} - Cliente {onu.sn_short || onu.sn?.slice(-8)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    SN: {onu.sn}
                                </Typography>
                            </Box>
                            
                            <Box textAlign="right" minWidth={80}>
                                <Chip 
                                    label={`${onu.incident_count} incidentes`}
                                    color={
                                        onu.incident_count >= 10 ? 'error' :
                                        onu.incident_count >= 5 ? 'warning' : 'default'
                                    }
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                />
                                <Typography variant="caption" display="block" color="textSecondary" mt={0.5}>
                                    Últimos 30 dias
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
            
            {data.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Atenção:</strong> As ONUs listadas acima precisam de manutenção preventiva urgente
                </Alert>
            )}
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Resumo Executivo de Incidentes
// =============================================================================
export function IncidentSummary() {
    const [summary, setSummary] = useState({
        total_last_24h: 0,
        total_last_7d: 0,
        most_common_cause: '',
        peak_hour: '',
        mttr: 0,
        availability: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/incident_summary');
                setSummary(response.data || {});
            } catch (error) {
                console.error('Erro ao buscar resumo:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={200} />;

    return (
        <Card elevation={2}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Montserrat', mb: 2 }}>
                    📊 Resumo Executivo - Incidentes OLT Huawei
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                        <SummaryMetric
                            icon={<ErrorIcon sx={{ color: red[600], fontSize: 32 }} />}
                            value={summary.total_last_24h}
                            label="Últimas 24h"
                            color={red[600]}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <SummaryMetric
                            icon={<NetworkCheckIcon sx={{ color: green[600], fontSize: 32 }} />}
                            value={`${summary.availability}%`}
                            label="Disponibilidade"
                            color={green[600]}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <SummaryMetric
                            icon={<WarningIcon sx={{ color: orange[600], fontSize: 32 }} />}
                            value={summary.most_common_cause || 'N/A'}
                            label="Principal Alarme"
                            color={orange[600]}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <SummaryMetric
                            icon={<PowerOffIcon sx={{ color: purple[600], fontSize: 32 }} />}
                            value={summary.peak_hour ? `${summary.peak_hour}:00` : 'N/A'}
                            label="Horário de Pico"
                            color={purple[600]}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================

function ChartContainer({ title, children }) {
    return (
        <Paper elevation={2} sx={{ p: 2, height: 'fit-content' }}>
            <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                    fontFamily: 'Montserrat', 
                    fontWeight: 500,
                    mb: 2
                }}
            >
                {title}
            </Typography>
            {children}
        </Paper>
    );
}

function SummaryMetric({ icon, value, label, color }) {
    return (
        <Box textAlign="center">
            {icon && <Box mb={1}>{icon}</Box>}
            <Typography 
                variant="h5" 
                sx={{ 
                    fontWeight: 'bold',
                    color: color || 'text.primary',
                    fontFamily: 'Montserrat'
                }}
            >
                {value}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {label}
            </Typography>
        </Box>
    );
}

function LoadingCard({ height = 200 }) {
    return (
        <Paper 
            elevation={2} 
            sx={{
                height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <CircularProgress />
        </Paper>
    );
}